(ns client-portal.client.ai-agent.history.data
  "Past-chat history for the full-page Agent view's right panel: the user's own
   sessions with the selected agent, newest first, paged in as the panel scrolls.

   Not scoped to the header's concept/location — every past chat is listed, and each
   row carries the concept/location it was held under so `:ai-agent/open-session`
   (chat.data) can reopen it on that view.

   Pages are keyed by agent under `chat.data/history-path` and accumulated: page n is
   its own slot, so the in-flight page's `:loading` never blanks the pages already
   shown. The server pages by keyset, so the request for page n+1 sends the last row
   of page n rather than an offset — a new turn moves its session to the head of the
   list, which an offset would skip or repeat rows over."
  (:require [client-portal.client.ai-agent.chat.data :as chat.d]
            [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.locations.data :as locations.d]
            [shared.core.reactive :as re]
            [shared.core.util :as util]))

(def page-size 25)

(defn- agent-pages-path [db]
  ;; Keyed by agent: the history belongs to the agent, so switching agents shows that
  ;; agent's own list rather than refetching over the previous one's.
  (conj chat.d/history-path (str (chat.d/agent-id db)) :pages))

(defn- pages [db]
  (get-in db (agent-pages-path db)))

(defn- last-page [db]
  (let [ps (pages db)]
    (get ps (dec (count ps)))))

(defn sessions
  "Every page loaded so far, flattened in order."
  [db]
  (->> (pages db)
       (sort-by key)
       (mapcat #(:sessions (util/when-not-loading (val %))))
       (vec)))

(defn has-more? [db]
  ;; A page still loading reports no more, which also keeps the scroll handler from
  ;; asking for the page after it before this one lands.
  (boolean (:has-more (util/when-not-loading (last-page db)))))

(defn loading? [db]
  (boolean (some util/loading? (vals (pages db)))))

(re/reg-sub :ai-agent/session-history-has-more?- has-more?)
(re/reg-sub :ai-agent/session-history-loading?- loading?)

(re/reg-auth0-loader
  :ai-agent/load-session-history
  ;; Dispatched with no args, so the page to fill — the one after those already in the
  ;; db — is derived here and in the request below from the same db.
  (fn [_ db]
    (conj (agent-pages-path db) (count (pages db))))
  (fn [db & _]
    (let [agent-id (not-empty (chat.d/agent-id db))
          {:keys [id last-modified-at]} (last (sessions db))]
      ;; One page in flight at a time. The sub below asks on every render until a page
      ;; slot exists, and the auth0 token round-trip sits between the ask and the slot
      ;; being marked — so without this a second request goes out, and with no page
      ;; loaded yet it has no cursor to send and just writes the first page again into
      ;; the next slot. Requiring a cursor for anything but the first page says the same
      ;; thing from the other side.
      (when (and agent-id
                 (not (loading? db))
                 (or (empty? (pages db)) id))
        {:method :get
         :url "/api/ai-agent/sessions"
         :query-params (cond-> {:agent-id agent-id
                                :limit page-size}
                         id
                         (assoc :before-id id
                                :before-last-modified-at last-modified-at))})))
  :transf #(some-> % :body (util/->kebab-case-keywords))
  :error-transf (fn [] {:sessions [] :has-more false}))

(defn- pending-session
  "A stand-in row for the turn that is creating a session. The first message of a new
   chat is sent long before the server answers with a session id, so without this the
   conversation is missing from its own history for the length of the turn. The real
   row replaces it when the answer lands and drops the cached pages. Kept out of
   `sessions` so it can never be read as a page cursor."
  [db]
  (when (and (chat.d/pending-message db) (nil? (chat.d/session-id db)))
    {:id ::pending
     :title "New Chat"
     ;; `:label` rather than the brand display name — it is what the list query returns
     ;; for the real row, so the swap doesn't rename the concept or location.
     :concept-label (:label (concepts.d/current-concept db))
     :location-id (locations.d/current-selected-location-id db)
     :location-label (:label (locations.d/current-location db))
     :pending? true}))

(re/reg-sub
  :ai-agent/session-history-
  (fn [db]
    ;; Loads the first page the first time the panel renders; later pages come from the
    ;; panel's scroll handler.
    (when (and (nil? (pages db)) (not-empty (chat.d/agent-id db)))
      (re/dispatch-load [:ai-agent/load-session-history]))
    (vec (concat (when-let [pending (pending-session db)]
                   [pending])
                 (sessions db)))))
